import { useEffect } from 'react';

import { useQrContext } from '../state/qr-context';

function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target.isContentEditable
  );
}

/**
 * Undo/redo keyboard shortcuts.
 *
 * `event.key` is compared case-insensitively because holding Shift reports
 * `"Z"`, not `"z"` — which is why Ctrl+Shift+Z previously did nothing at all.
 *
 * Ctrl+R is deliberately *not* bound. The previous version hijacked it to
 * reset the form, replacing the browser's reload with a destructive action
 * that could not be undone.
 */
export function useKeyboardShortcuts(): void {
  const { actions, canUndo, canRedo } = useQrContext();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (!event.ctrlKey && !event.metaKey) return;
      // Let text fields keep their native undo stack.
      if (isTextEntry(event.target)) return;

      const key = event.key.toLowerCase();

      if (key === 'z' && !event.shiftKey) {
        if (!canUndo) return;
        event.preventDefault();
        actions.undo();
        return;
      }

      if ((key === 'z' && event.shiftKey) || key === 'y') {
        if (!canRedo) return;
        event.preventDefault();
        actions.redo();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [actions, canUndo, canRedo]);
}
